import { type ReactNode } from 'react';

import { Avatar, InitialsAvatar } from '@/components/ui/Avatar';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { TeamLabel } from './team-label';

import type { WikiPersonDetail } from '../people-wiki.types';

const AVATAR_CLASS_NAME = 'size-14';

/** The top of a person's page. The editors come in as slots, so this component only shows the person. */
export const PersonHeader = ({
  person,
  nameEditor,
  aliases,
  accounts,
  actions,
}: {
  person: WikiPersonDetail;
  nameEditor: ReactNode;
  aliases: ReactNode;
  accounts: ReactNode;
  /** The buttons at the end of the top row, which stay together as one item. */
  actions: ReactNode;
}) => {
  const emails = [person.primaryEmail, ...person.otherEmails].filter(
    (email) => email !== null
  );
  const initials = (
    <InitialsAvatar
      name={person.name}
      seed={person.primaryEmail ?? person.id}
      className={cn(AVATAR_CLASS_NAME, 'rounded-lg text-base')}
    />
  );

  return (
    <YStack className="gap-4">
      <XStack className="items-start justify-between gap-2">
        <XStack wrap className="gap-2">
          {person.avatarUrls.length === 0
            ? initials
            : person.avatarUrls.map((avatarUrl) => (
                <Avatar
                  key={avatarUrl}
                  avatarUrl={avatarUrl}
                  alt={person.name}
                  imageClassName="size-full rounded-lg object-cover"
                  containerClassName={cn(
                    'flex shrink-0 overflow-hidden rounded-lg border border-border',
                    AVATAR_CLASS_NAME
                  )}
                  fallback={initials}
                />
              ))}
        </XStack>
        {/* An empty group, as on the user's own page, must not leave a gap */}
        <XStack className="items-center gap-2 empty:hidden">{actions}</XStack>
      </XStack>
      <YStack className="gap-0.5">
        <XStack className="items-center gap-2">
          {nameEditor}
          {person.isTeammate && <TeamLabel />}
        </XStack>
        {emails.length > 0 && (
          <span className="select-text text-sm text-foreground-muted">
            {emails.join(' · ')}
          </span>
        )}
      </YStack>
      {aliases}
      {accounts}
    </YStack>
  );
};
